import React, { useState, Suspense } from 'react'
import { motion } from 'framer-motion'
import { Shield, Sparkles, Swords, UserIcon, Zap } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { ArcadePageShell } from '../components/layout/ArcadePageShell'
import { FlameKnight, ShadowNinja, CyberMech, ForestDruid, StormMonk } from '../components/characters'
import { NeonCity } from '../components/stages'

const characterComponents = {
  flameKnight: FlameKnight,
  shadowNinja: ShadowNinja,
  cyberMech: CyberMech,
  forestDruid: ForestDruid,
  stormMonk: StormMonk,
  brawler: FlameKnight,
  ranger: ShadowNinja,
  striker: ForestDruid,
  tank: CyberMech,
}

const characterColors = {
  flameKnight: '#ef4444',
  shadowNinja: '#a855f7',
  cyberMech: '#06b6d4',
  forestDruid: '#84cc16',
  stormMonk: '#eab308',
  brawler: '#ef4444',
  ranger: '#a855f7',
  striker: '#84cc16',
  tank: '#06b6d4',
}

const statIcons = [
  ['power', 'Attack', Swords],
  ['speed', 'Speed', Zap],
  ['defense', 'Defense', Shield],
  ['jump', 'Special', Sparkles],
]

export function Characters() {
  const { state, dispatch, characterArchetypes } = useApp()
  const [showCreator, setShowCreator] = useState(false)
  const [newCharacter, setNewCharacter] = useState({
    name: '',
    archetype: 'flameKnight',
  })

  const currentUser = state.users.find(u => u.id === state.currentUserId)
  const myCharacters = state.characters.filter(c => c.userId === state.currentUserId || c.ownerId === state.currentUserId)
  const selectedCharacter = myCharacters.find(c => c.id === state.selectedCharacterId) || myCharacters[0]

  const handleCreate = (e) => {
    e.preventDefault()
    if (!newCharacter.name.trim()) return

    const archetype = characterArchetypes[newCharacter.archetype]
    const character = {
      id: `char_${Date.now()}`,
      userId: state.currentUserId,
      name: newCharacter.name.trim(),
      archetype: newCharacter.archetype,
      ...archetype.stats,
      maxJumps: archetype.maxJumps,
      specialName: archetype.specialName,
      createdAt: Date.now(),
    }

    dispatch({ type: 'ADD_CHARACTER', payload: character })
    setNewCharacter({ name: '', archetype: 'flameKnight' })
    setShowCreator(false)
  }

  const handleDelete = (id) => {
    if (confirm('Delete this fighter?')) {
      dispatch({ type: 'DELETE_CHARACTER', payload: id })
    }
  }

  const handleSelect = (id, isCpu = false) => {
    if (isCpu) {
      dispatch({ type: 'SELECT_CPU_CHARACTER', payload: id })
    } else {
      dispatch({ type: 'SELECT_CHARACTER', payload: id })
    }
  }

  const getCharacterStats = (character) => {
    const archetype = characterArchetypes[character?.archetype] || characterArchetypes.flameKnight
    return character?.stats || {
      power: character?.power ?? archetype.stats.power,
      speed: character?.speed ?? archetype.stats.speed,
      defense: character?.defense ?? archetype.stats.defense,
      jump: character?.jump ?? archetype.stats.jump,
    }
  }

  if (!currentUser) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold mb-4">Sign in to Create Fighters</h2>
        <p className="text-gray-400">Create an account to build your roster</p>
      </div>
    )
  }

  const archetype = characterArchetypes[selectedCharacter?.archetype] || characterArchetypes.flameKnight
  const SelectedSvg = characterComponents[selectedCharacter?.archetype] || FlameKnight
  const selectedColor = characterColors[selectedCharacter?.archetype] || '#06b6d4'
  const selectedStats = getCharacterStats(selectedCharacter)

  return (
    <ArcadePageShell Background={NeonCity}>
    <div className="flex min-h-[calc(100vh-12rem)] flex-col gap-4 overflow-y-auto lg:overflow-hidden">
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="flex shrink-0 items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <UserIcon className="text-cyan-500/50" size={22} />
          <h1 className="text-3xl font-black uppercase italic tracking-tight drop-shadow-lg">Characters</h1>
        </div>
        <button onClick={() => setShowCreator(true)} className="rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 px-4 py-2 text-sm font-black uppercase italic tracking-wider text-black transition-all hover:from-cyan-400 hover:to-blue-500">
          New Fighter
        </button>
      </motion.div>

      {showCreator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-white/10 bg-[#131a33] p-6">
            <h2 className="mb-4 text-xl font-black uppercase italic">Create New Fighter</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-400">Name</label>
                <input type="text" value={newCharacter.name} onChange={(e) => setNewCharacter({ ...newCharacter, name: e.target.value })} className="w-full rounded-lg border border-white/10 bg-black/30 px-4 py-2 text-white focus:border-cyan-500/50 focus:outline-none" placeholder="Fighter name" maxLength={20} autoFocus />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-400">Fighter Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(characterArchetypes).map(([key, type]) => {
                    const CharacterSvg = characterComponents[key] || FlameKnight
                    return (
                      <button key={key} type="button" onClick={() => setNewCharacter({ ...newCharacter, archetype: key })} className={`rounded-lg p-3 text-left transition-all ${newCharacter.archetype === key ? 'border border-cyan-500/50 bg-cyan-500/20' : 'border border-white/10 bg-black/30 hover:border-white/30'}`}>
                        <div className="mb-2 h-24"><Suspense fallback={<div className="h-full rounded bg-white/5" />}><CharacterSvg className="h-full w-full" /></Suspense></div>
                        <div className="text-sm font-medium">{type.label}</div>
                        <div className="text-xs text-gray-500">{type.role}</div>
                      </button>
                    )
                  })}
                </div>
              </div>
              <div className="rounded-lg bg-black/30 p-3">
                <p className="text-sm text-gray-400">{characterArchetypes[newCharacter.archetype].description}</p>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowCreator(false)} className="flex-1 rounded-lg border border-white/10 bg-white/5 py-2 transition-all hover:bg-white/10">Cancel</button>
                <button type="submit" className="flex-1 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 py-2 font-bold text-black transition-all hover:from-cyan-400 hover:to-blue-500">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {myCharacters.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-white/10 bg-white/5 p-10 text-center">
          <Swords className="mb-4 text-cyan-400" size={42} />
          <h2 className="mb-2 text-2xl font-black uppercase italic">No Fighters Yet</h2>
          <p className="mb-4 text-gray-400">Create your first fighter to start battling.</p>
          <button onClick={() => setShowCreator(true)} className="rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-2 font-bold text-black">Create Fighter</button>
        </div>
      ) : (
        <>
          <div className="flex flex-1 flex-col gap-4 lg:flex-row">
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }} className="relative flex min-h-[340px] flex-1 flex-col overflow-hidden rounded-xl border-2 bg-gray-900/60 backdrop-blur-sm transition-colors duration-500" style={{ borderColor: `${selectedColor}66` }}>
              <div className="absolute left-0 top-0 h-1 w-full" style={{ backgroundColor: selectedColor, boxShadow: `0 0 20px ${selectedColor}` }} />
              <div className="pointer-events-none absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 50% 60%, ${selectedColor}, transparent 70%)` }} />
              <div className="relative z-10 flex flex-1 flex-col items-center gap-4 p-5 lg:flex-row">
                <motion.div key={selectedCharacter.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex h-52 w-40 shrink-0 items-center justify-center lg:h-64 lg:w-48">
                  <SelectedSvg className="h-full w-full drop-shadow-2xl" />
                </motion.div>
                <div className="w-full min-w-0 flex-1">
                  <motion.div key={`${selectedCharacter.id}-info`} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}>
                    <h2 className="text-3xl font-black uppercase italic tracking-tight lg:text-4xl" style={{ color: selectedColor, textShadow: `0 0 15px ${selectedColor}80` }}>{selectedCharacter.name}</h2>
                    <p className="mt-1 text-sm font-bold uppercase tracking-widest text-gray-400">{archetype.label} / {archetype.role}</p>
                    <p className="mt-2 text-sm text-gray-500">Special: <span className="text-cyan-400">{selectedCharacter.specialName || archetype.specialName}</span></p>
                  </motion.div>
                  <div className="mt-4 space-y-2.5">
                    {statIcons.map(([key, label, Icon]) => {
                      const value = selectedStats[key] ?? 5
                      return (
                        <div key={key} className="flex items-center gap-3">
                          <Icon size={14} className="shrink-0 text-gray-500" />
                          <span className="w-16 text-[11px] font-bold uppercase tracking-wider text-gray-500">{label}</span>
                          <div className="h-2 flex-1 overflow-hidden rounded-full bg-black/40">
                            <motion.div key={`${selectedCharacter.id}-${key}`} initial={{ width: 0 }} animate={{ width: `${Math.min(value * 10, 100)}%` }} transition={{ duration: 0.5 }} className="h-full rounded-full" style={{ backgroundColor: selectedColor, boxShadow: `0 0 8px ${selectedColor}` }} />
                          </div>
                          <span className="w-8 text-right text-[11px] font-black text-gray-400">{value}</span>
                        </div>
                      )
                    })}
                  </div>
                  <div className="mt-5 flex gap-2">
                    <button onClick={() => handleSelect(selectedCharacter.id, false)} disabled={state.selectedCharacterId === selectedCharacter.id} className="flex-1 rounded bg-cyan-500/10 py-2 text-xs font-bold uppercase tracking-wider text-cyan-400 transition-all hover:bg-cyan-500/20 disabled:cursor-default disabled:bg-cyan-500/20">{state.selectedCharacterId === selectedCharacter.id ? 'Selected P1' : 'Select P1'}</button>
                    <button onClick={() => handleSelect(selectedCharacter.id, true)} disabled={state.selectedCpuCharacterId === selectedCharacter.id} className="flex-1 rounded bg-pink-500/10 py-2 text-xs font-bold uppercase tracking-wider text-pink-400 transition-all hover:bg-pink-500/20 disabled:cursor-default disabled:bg-pink-500/20">{state.selectedCpuCharacterId === selectedCharacter.id ? 'Selected CPU' : 'Select CPU'}</button>
                    <button onClick={() => handleDelete(selectedCharacter.id)} className="rounded border border-white/10 px-3 text-gray-500 transition-colors hover:border-red-500/40 hover:text-red-400">×</button>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="flex shrink-0 gap-2 overflow-x-auto pb-2 lg:w-72 lg:flex-col lg:overflow-y-auto lg:pb-0">
              {myCharacters.map((character, index) => {
                const isSelected = selectedCharacter.id === character.id
                const isP1 = state.selectedCharacterId === character.id
                const isCpu = state.selectedCpuCharacterId === character.id
                const CharacterSvg = characterComponents[character.archetype] || FlameKnight
                const color = characterColors[character.archetype] || '#06b6d4'
                const type = characterArchetypes[character.archetype] || characterArchetypes.flameKnight
                return (
                  <motion.button key={character.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + index * 0.05 }} onClick={() => handleSelect(character.id, false)} className={`group relative flex w-32 shrink-0 items-center gap-3 overflow-hidden rounded-lg border-2 p-2 transition-all duration-300 lg:w-full ${isSelected ? 'scale-[1.02] bg-black/40' : 'border-white/10 bg-gray-900/40 opacity-60 hover:border-white/30 hover:opacity-100'}`} style={isSelected ? { borderColor: color, boxShadow: `0 0 15px ${color}55` } : {}}>
                    <div className="flex h-14 w-12 shrink-0 items-center justify-center overflow-hidden rounded border bg-black/50" style={{ borderColor: isSelected ? color : 'rgba(255,255,255,0.1)' }}>
                      <CharacterSvg className="mt-1 h-14 w-10 transition-transform duration-300 group-hover:scale-110" />
                    </div>
                    <div className="hidden min-w-0 flex-1 text-left lg:block">
                      <span className="block truncate text-xs font-black uppercase" style={{ color: isSelected ? color : '#9ca3af' }}>{character.name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-gray-600">{type.label}</span>
                    </div>
                    {(isP1 || isCpu) && <span className="absolute right-2 top-2 rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] font-black text-white">{isP1 ? 'P1' : 'CPU'}</span>}
                  </motion.button>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
    </ArcadePageShell>
  )
}
